// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageCarousel from "@insite/content-library/Components/ProductImageCarousel";
import { ProductDetailsPrimaryImageStyles } from "@insite/content-library/Widgets/ProductDetails/ProductDetailsPrimaryImage";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    getScratchAndDentCondition,
    getScratchAndDentContactInfo,
    getScratchAndDentHold,
    getScratchAndDentLocation,
    getScratchAndDentSellPrice,
} from "@plumberssupply/App/Store/Selectors/ScratchAndDentSelectors";
import ProductPrimaryImage from "@plumberssupply/Overrides/Components/ProductPrimaryImage";
import ProductQuantityOrdered from "@plumberssupply/Overrides/Components/ProductQuantityOrdered";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    showImage: boolean;
    showLocation: boolean;
    showPrice: boolean;
    showCondition: boolean;
    showContact: boolean;
    showAddToCart: boolean;
    extendedStyles?: ProductDetailsScratchAndDentCarouselProductStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsScratchAndDentCarouselProductStyles {
    container?: InjectableCss;
    imageContainer?: InjectableCss;
    infoContainer?: InjectableCss;
    actionContainer?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productInfoContainer?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    price?: TypographyPresentationProps;
    productQuantityOrderedStyles?: TextFieldProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    viewGallery?: LinkPresentationProps;
    primaryImageStyles?: ProductDetailsPrimaryImageStyles;
}

export const productDetailsScratchAndDentCarouselProductStyles: ProductDetailsScratchAndDentCarouselProductStyles = {};

const ProductDetailsScratchAndDentCarouselProduct: React.FC<Props> = ({
    product,
    productInfo,
    showImage,
    showLocation,
    carouselId,
    showPrice,
    showCondition,
    showContact,
    showAddToCart,
    extendedStyles,
    updateProductInfo,
}) => {
    const [styles] = React.useState(() =>
        mergeWithCss(productDetailsScratchAndDentCarouselProductStyles, extendedStyles),
    );

    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    const selectImageHandler = (index: number) => {
        setSelectedImageIndex(index);
    };

    const productLocation = getScratchAndDentLocation(product.properties);
    const productPrice = getScratchAndDentSellPrice(product.properties);
    const productCondition = getScratchAndDentCondition(product.properties);
    const productContact = getScratchAndDentContactInfo(product.properties);

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            updateQuantity(qtyOrdered);
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateUnitOfMeasure(unitOfMeasure);
        },
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.imageContainer}>
                    {showImage && <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />}
                    {product.images && product.images.length > 1 && (
                        <Link {...styles.viewGallery} onClick={() => setFullImageModalIsOpen(true)}>
                            {translate("View Gallery")}
                        </Link>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.infoContainer}>
                    {product.brand && <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />}
                    <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                    <StyledWrapper {...styles.productInfoContainer}>
                        <Typography {...styles.infoLabel}>{translate("Part #")}:</Typography>
                        <Typography {...styles.infoValue}>{product.productNumber}</Typography>
                    </StyledWrapper>
                    {showLocation && productLocation && (
                        <StyledWrapper {...styles.productInfoContainer}>
                            <Typography {...styles.infoLabel}>{translate("Location")}:</Typography>
                            <Typography {...styles.infoValue}>{productLocation}</Typography>
                        </StyledWrapper>
                    )}
                    {showCondition && productCondition && (
                        <StyledWrapper {...styles.productInfoContainer}>
                            <Typography {...styles.infoLabel}>{translate("Condition")}:</Typography>
                            <Typography {...styles.infoValue}>{productCondition}</Typography>
                        </StyledWrapper>
                    )}
                    {showContact && productContact && (
                        <StyledWrapper {...styles.productInfoContainer}>
                            <Typography {...styles.infoLabel}>{translate("Contact")}:</Typography>
                            <Typography {...styles.infoValue}>{productContact}</Typography>
                        </StyledWrapper>
                    )}
                    {productPrice && <Typography {...styles.price}>${productPrice}</Typography>}
                </StyledWrapper>
                <StyledWrapper {...styles.actionContainer}>
                    {showAddToCart && (
                        <>
                            <ProductQuantityOrdered
                                labelOverride=""
                                showActions={true}
                                disabled={getScratchAndDentHold(product.properties)}
                                extendedStyles={styles.productQuantityOrderedStyles}
                            />
                            <ProductAddToCartButton
                                data-test-selector="addToCartBtn"
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            {product.images && product.images.length > 1 && (
                <Modal
                    {...styles.primaryImageStyles?.fullPrimaryImageModal}
                    isOpen={fullPrimaryImageModalIsOpen}
                    handleClose={() => setFullImageModalIsOpen(false)}
                >
                    <ProductPrimaryImage
                        product={product}
                        image={product.images[selectedImageIndex]}
                        useLargeImage={true}
                        extendedStyles={styles.primaryImageStyles?.fullPrimaryImage}
                    />
                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        onSelectImage={selectImageHandler}
                        extendedStyles={styles.primaryImageStyles?.imageCarousel}
                    />
                </Modal>
            )}
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetailsScratchAndDentCarouselProduct);
